import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { IsFeatureReady } from "src/constants/features";
import { DangerButton, LoadingActionButton, SharedTimeInput } from "src/components";
import { UserKeys } from "src/constants/userDetails";
import GreenCheckIcon from "src/img/new/green-check.svg";
import {
    FilterItem,
    FilterLabel,
    ColorSwatch,
    Toggle,
    SmallText,
    Badge,
    SourceChipRow,
    CategoryTab,
    CategoryTabCheck,
    SuggestionCard,
    SuggestionActions,
    LocationFieldWrap,
    LocationSuggestionList,
    LocationSuggestionButton,
    SuggestionRestoreRow,
    FieldHint,
    TimezoneFlagBadge,
    TimezoneTimeline,
    TimezoneMatchList,
    TimezoneMatchItem,
    TimezoneMatchRole,
    TimezoneHourButton,
    TimezoneHourBar,
    TimezoneSuggestionGrid,
    ModalCard,
    ModalHeaderBlock,
    ModalScroll,
    ModalFooter,
    ModalHeaderTop,
    ModalTitleBlock,
    ModalTitle,
    ModalSection,
    ModalSectionHeader,
    ModalSectionTitle,
    ModalSectionNote,
    AttachmentRow,
    HiddenFileInput,
    AttachmentList,
    AttachmentBadge,
    AttachmentName,
    AttachmentRemove,
    FormGrid,
    VisibilityTargetGrid,
    VisibilityTargetCard,
    VisibilityTargetHeader,
    VisibilityTargetTitle,
    VisibilityTargetActions,
    VisibilityPickerPanel,
    VisibilityPickerHeader,
    VisibilityUserList,
    VisibilityUserRow,
    VisibilityUserAvatar,
    VisibilityEmptyState,
    Label,
    ModalPrimaryButton,
    ModalGradientButton,
    SuggestionRestoreButton,
    ModalCancelButton,
    TaskFieldInput,
    TaskFieldSelect,
    TaskFieldArea,
    ErrorText,
    CalendarDateInput,
    CalendarSelectWrap,
    CalendarSelectChevron,
    CalendarMeetingSection,
} from "..";
import { getCalendarCompanyUsers, getDetectedLocationLabel, searchCalendarLocations, uploadCalendarAttachment } from "../../api";
import {
    AUDIENCE_COLORS,
    createCalendarItemFromForm,
    getCompanyUserInitials,
    getCompanyUserName,
    SHARED_AUDIENCE,
    SOURCE_COLORS,
    TASK_PRIORITIES,
    TASK_REPEAT_LABEL,
    TASK_REPEAT_OPTIONS,
    TASK_TYPES,
} from "../../calendarData";
import { getItemTypeColor, isItemTypeAvailableForAudience } from "../../itemTypes";
import type {
    CalendarItem,
    CalendarItemForm,
    CalendarUser,
    CompanyUser,
    CompanyUsersStatus,
    LocationInfo,
    LocationSuggestion,
    ManagedItemType,
    SharedAudience,
    SmartTimeSuggestion,
    TaskPrefill,
    TaskPriority,
    TaskRepeatOption,
    ThemeMode,
    TimezoneCalculator,
    TimezoneHourSlot,
    VisibilityGroupKey,
    VisibilityRecipientState,
} from "../../types";
import {
    REMINDER_OPTIONS,
    DEFAULT_START_TIME,
    DEFAULT_END_TIME,
    ModalErrors,
    getVisibilityRecipientGroup,
    getAudienceOptionsUserCanManage,
    getAudienceRecipientGroups,
    normaliseVisibilityRecipients,
    getVisibilityRecipientsForAudience,
    getVisibilityRecipientSummary,
    getUserString,
    getCalendarUserEmployeeUuid,
    calendarItemFormDefaults,
    canManageCalendarAudience,
    canManageSharedCalendarItems,
    hasScheduledCalendarFields,
    getSuggestedDuration,
    getSmartTimeSuggestion,
    getTimezoneFlag,
    buildTimezoneCalculator,
    syncTaskRangeFromStartDate,
    syncTaskRangeFromEndDate,
    syncRangeFromStartDate,
    syncRangeFromEndDate
} from "../../utils/calendarHelpers";

type CalendarItemModalProps = {
    themeMode: ThemeMode;
    user: CalendarUser | null | undefined;
    item: CalendarItem | null;
    prefillTask: TaskPrefill | null;
    hideSmartSuggestion: boolean;
    selectedDate: Dayjs;
    visibleScheduledItems: CalendarItem[];
    visibleTasks: CalendarItem[];
    itemTypes: ManagedItemType[];
    locationInfo: LocationInfo | null;
    timezone: string;
    timezoneAttendees: string[];
    onClose: () => void;
    onSave: (item: CalendarItem) => void | Promise<void>;
    onDelete: (item: CalendarItem) => void | Promise<void>;
};

const hasMeetingParticipantVisibility = (item: CalendarItem | CalendarItemForm): boolean => {
    if (!item.meeting || !("enabled" in item.meeting ? item.meeting.enabled : true)) {
        return false;
    }
    const recipients = normaliseVisibilityRecipients(item.visibilityRecipients);
    if (recipients.employees.all || recipients.clients.all) {
        return false;
    }
    const visibilityUuids = Array.from(new Set([
        ...recipients.employees.selectedEmployeeUuids,
        ...recipients.clients.selectedEmployeeUuids,
    ])).sort();
    const participantUuids = Array.from(new Set(item.meeting.internalParticipantUuids)).sort();
    return item.audience !== SHARED_AUDIENCE.PERSONAL
        && visibilityUuids.length > 0
        && visibilityUuids.length === participantUuids.length
        && visibilityUuids.every((uuid, index) => uuid === participantUuids[index]);
};

export function CalendarItemModal({ themeMode, user, item, prefillTask, hideSmartSuggestion, selectedDate, visibleScheduledItems, visibleTasks, itemTypes, locationInfo, timezone, timezoneAttendees, onClose, onSave, onDelete }: CalendarItemModalProps) {
    const isScheduledMode = hasScheduledCalendarFields(item);
    const [form, setForm] = useState<CalendarItemForm>(calendarItemFormDefaults(item || prefillTask, selectedDate));
    const [errors, setErrors] = useState<ModalErrors>({});
    const [suggestionDismissed, setSuggestionDismissed] = useState(false);
    const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
    const [locationSearchPending, setLocationSearchPending] = useState(false);
    const [locationSearchOpen, setLocationSearchOpen] = useState(false);
    const [timezoneCalculatorOpen, setTimezoneCalculatorOpen] = useState(false);
    const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
    const [companyUsersStatus, setCompanyUsersStatus] = useState<CompanyUsersStatus>("idle");
    const [recipientPickerGroup, setRecipientPickerGroup] = useState<VisibilityGroupKey | null>(null);
    const [recipientSearch, setRecipientSearch] = useState("");
    const [savingTask, setSavingTask] = useState(false);
    const [deletingItem, setDeletingItem] = useState(false);
    const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState<File[]>([]);
    const attachmentInputRef = useRef<HTMLInputElement | null>(null);
    const timezoneCalculatorRef = useRef<HTMLDivElement | null>(null);
    const companyUsersRequestRef = useRef<AbortController | null>(null);
    const locationBlurTimeoutRef = useRef<number | null>(null);
    const locationAutofilledRef = useRef(false);
    const canManageSharedCalendar = canManageSharedCalendarItems(user);
    const availableItemTypeOptions = useMemo(() => itemTypes.filter(itemType => (
        canManageSharedCalendar || itemType.scope !== "company"
    )), [canManageSharedCalendar, itemTypes]);
    const audienceOptions = useMemo(() => getAudienceOptionsUserCanManage(user), [user]);
    const getManagedItemTypeByValue = (value: string | undefined): ManagedItemType | undefined => {
        if (!value) return undefined;
        return itemTypes.find(itemType => itemType.itemTypeUuid === value || itemType.id === value);
    };
    const getFallbackItemType = (): ManagedItemType | undefined => (
        itemTypes.find(itemType => itemType.id === TASK_TYPES.TASK && (canManageSharedCalendar || itemType.scope !== "company"))
        || itemTypes.find(itemType => canManageSharedCalendar || itemType.scope !== "company")
    );
    const selectedItemType = getManagedItemTypeByValue(form.itemTypeUuid || form.taskType);
    const supportsMeets = IsFeatureReady.publicMeetings() && (
        selectedItemType?.id === TASK_TYPES.MEETING
        || (!selectedItemType && (form.itemTypeUuid || form.taskType) === TASK_TYPES.MEETING)
        || Boolean(item?.meeting)
    );
    const itemActionPending = savingTask || deletingItem;

    const handleDelete = async (): Promise<void> => {
        if (!item || itemActionPending) {
            return;
        }
        setDeletingItem(true);
        try {
            await onDelete(item);
        } finally {
            setDeletingItem(false);
        }
    };

    useEffect(() => {
        const defaults = calendarItemFormDefaults(item || prefillTask, selectedDate);
        const currentItemType = getManagedItemTypeByValue(defaults.itemTypeUuid || defaults.taskType);
        const fallbackItemType = !isScheduledMode && !canManageSharedCalendar && currentItemType?.scope === "company"
            ? getFallbackItemType()
            : null;
        const nextDefaults = fallbackItemType
            ? {
                ...defaults,
                itemTypeUuid: fallbackItemType.itemTypeUuid,
                taskType: fallbackItemType.itemTypeUuid,
                color: getItemTypeColor(fallbackItemType.itemTypeUuid, itemTypes, defaults.color),
            }
            : defaults;
        setForm(!canManageSharedCalendar && (!item || item.audience === SHARED_AUDIENCE.PERSONAL)
            ? {
                ...nextDefaults,
                audience: SHARED_AUDIENCE.PERSONAL,
                visibilityRecipients: getVisibilityRecipientsForAudience(SHARED_AUDIENCE.PERSONAL, nextDefaults.visibilityRecipients),
            }
            : nextDefaults);
        setTimezoneCalculatorOpen(false);
        setRecipientPickerGroup(null);
        setRecipientSearch("");
        setPendingAttachmentFiles([]);
        if (attachmentInputRef.current) {
            attachmentInputRef.current.value = "";
        }
    }, [canManageSharedCalendar, isScheduledMode, item, prefillTask, selectedDate]);

    useEffect(() => {
        if (itemTypes.length === 0) return;
        setForm((prev) => {
            const currentItemType = getManagedItemTypeByValue(prev.itemTypeUuid || prev.taskType);
            const nextItemType = !canManageSharedCalendar && currentItemType?.scope === "company"
                ? getFallbackItemType()
                : currentItemType || getFallbackItemType();
            if (!nextItemType || prev.itemTypeUuid === nextItemType.itemTypeUuid) return prev;
            return {
                ...prev,
                itemTypeUuid: nextItemType.itemTypeUuid,
                taskType: nextItemType.itemTypeUuid,
                color: getItemTypeColor(nextItemType.itemTypeUuid, itemTypes, prev.color),
            };
        });
    }, [itemTypes]);

    useEffect(() => () => {
        companyUsersRequestRef.current?.abort();
    }, []);

    const handleAttachmentBrowse = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        // Chrome clears its live FileList when the input value is reset.
        const files = Array.from(target.files || []);
        target.value = "";
        if (files.length === 0) return;
        setPendingAttachmentFiles(prev => [...prev, ...files]);
        setErrors(prev => ({ ...prev, attachments: undefined }));
    };

    const removeAttachment = (attachmentUuid: string) => {
        setForm(prev => ({
            ...prev,
            attachments: prev.attachments.filter(attachment => attachment.uuid !== attachmentUuid),
        }));
    };

    const removePendingAttachment = (index: number) => {
        setPendingAttachmentFiles(prev => prev.filter((_, attachmentIndex) => attachmentIndex !== index));
    };

    useEffect(() => {
        setSuggestionDismissed(false);
    }, [form.startDate, form.taskType]);

    useEffect(() => {
        const detectedLocation = getDetectedLocationLabel(locationInfo);
        if (item || !detectedLocation || locationAutofilledRef.current) return;
        setForm((prev) => {
            if (prev.location.trim()) return prev;
            locationAutofilledRef.current = true;
            return {
                ...prev,
                location: detectedLocation,
            };
        });
    }, [item, locationInfo]);

    useEffect(() => {
        const query = form.location.trim();
        if (!locationSearchOpen || query.length < 3) {
            setLocationSearchPending(false);
            setLocationSuggestions([]);
            return undefined;
        }

        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => {
            setLocationSearchPending(true);
            searchCalendarLocations(query, controller.signal)
                .then((results) => {
                    setLocationSuggestions(results);
                })
                .catch((error) => {
                    if (error.name !== "AbortError") {
                        console.error(error);
                        setLocationSuggestions([]);
                    }
                })
                .finally(() => {
                    setLocationSearchPending(false);
                });
        }, 240);

        return () => {
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [form.location, locationSearchOpen]);

    useEffect(() => () => {
        if (locationBlurTimeoutRef.current) {
            window.clearTimeout(locationBlurTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        if (form.allDay && timezoneCalculatorOpen) {
            setTimezoneCalculatorOpen(false);
        }
    }, [form.allDay, timezoneCalculatorOpen]);

    useEffect(() => {
        if ((!recipientPickerGroup && !form.meeting.enabled) || companyUsersStatus !== "idle") return undefined;

        const controller = new AbortController();
        companyUsersRequestRef.current = controller;
        setCompanyUsersStatus("loading");

        getCalendarCompanyUsers(getUserString(user, UserKeys.TOKEN), controller.signal)
            .then(({ users, status }) => {
                if (controller.signal.aborted) return;
                setCompanyUsers(users);
                setCompanyUsersStatus(status);
            })
            .catch((error) => {
                if (controller.signal.aborted || error.name === "AbortError") return;
                console.error(error);
                setCompanyUsers([]);
                setCompanyUsersStatus("error");
            });

        return undefined;
    }, [companyUsersStatus, form.meeting.enabled, recipientPickerGroup, user]);

    const smartSuggestion = useMemo<SmartTimeSuggestion | null>(() => {
        if (form.allDay) return null;
        const baseDay = dayjs(form.startDate || selectedDate.format("YYYY-MM-DD"));
        return getSmartTimeSuggestion({
            selectedDay: baseDay,
            taskType: form.taskType,
            visibleScheduledItems,
            visibleTasks,
            timezone,
        });
    }, [form.startDate, form.taskType, selectedDate, timezone, visibleScheduledItems, visibleTasks]);

    const timezoneCalculator = useMemo<TimezoneCalculator>(() => {
        if (form.allDay || timezoneAttendees.length === 0) return { hours: [], recommendations: [], matchZones: [] };
        return buildTimezoneCalculator({
            selectedDay: dayjs(form.startDate || selectedDate.format("YYYY-MM-DD")),
            durationMinutes: getSuggestedDuration(form.taskType),
            timezone,
            attendeeTimezones: timezoneAttendees,
        });
    }, [form.allDay, form.startDate, form.taskType, selectedDate, timezone, timezoneAttendees]);

    const applyTimezoneSuggestion = (slot: SmartTimeSuggestion | TimezoneHourSlot) => {
        setForm(prev => ({
            ...prev,
            startDate: slot.startDate,
            startTime: slot.startTime,
            endDate: slot.endDate,
            endTime: slot.endTime,
        }));
    };

    const openTimezoneCalculator = () => {
        if (form.allDay) return;
        setTimezoneCalculatorOpen(true);
        window.setTimeout(() => {
            timezoneCalculatorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 0);
    };

    const activeRecipientGroups = getAudienceRecipientGroups(form.audience);
    const visibilityRecipients = normaliseVisibilityRecipients(form.visibilityRecipients);
    const selectedRecipientGroup: VisibilityGroupKey | undefined = recipientPickerGroup && activeRecipientGroups.includes(recipientPickerGroup)
        ? recipientPickerGroup
        : activeRecipientGroups[0];
    const recipientUsers = useMemo<CompanyUser[]>(() => {
        if (!selectedRecipientGroup) return [];
        const group = getVisibilityRecipientGroup(selectedRecipientGroup);
        const term = recipientSearch.trim().toLowerCase();
        return companyUsers
            .filter(companyUser => companyUser.isClient === group.isClient)
            .filter((companyUser) => {
                if (!term) return true;
                return [
                    getCompanyUserName(companyUser),
                    companyUser.email,
                    companyUser.department,
                    companyUser.employmentPosition,
                ].filter(Boolean).join(" ").toLowerCase().includes(term);
            });
    }, [companyUsers, recipientSearch, selectedRecipientGroup]);

    const updateScheduledAudience = (audience: SharedAudience) => {
        if (!canManageCalendarAudience(user, audience)) return;
        setForm(prev => ({ ...prev, audience }));
    };

    const updateAudience = (audience: SharedAudience) => {
        if (!canManageCalendarAudience(user, audience)) return;
        const currentItemTypeUuid = form.itemTypeUuid || form.taskType;
        const currentItemType = getManagedItemTypeByValue(currentItemTypeUuid);
        const fallbackItemType = getFallbackItemType();
        const nextItemTypeUuid = currentItemType && isItemTypeAvailableForAudience(currentItemType, audience)
            ? currentItemType.itemTypeUuid
            : fallbackItemType?.itemTypeUuid || currentItemTypeUuid;
        setForm(prev => ({
            ...prev,
            audience,
            itemTypeUuid: nextItemTypeUuid,
            taskType: nextItemTypeUuid,
            visibilityRecipients: getVisibilityRecipientsForAudience(audience, prev.visibilityRecipients),
            color: nextItemTypeUuid ? getItemTypeColor(nextItemTypeUuid, itemTypes, AUDIENCE_COLORS[audience]) : AUDIENCE_COLORS[audience],
        }));
        setRecipientPickerGroup(null);
        setRecipientSearch("");
    };

    const selectItemType = (itemType: ManagedItemType) => {
        if (!canManageSharedCalendar && itemType.scope === "company") return;
        const firstManagedSharedAudience = audienceOptions.find(({ value }) => value !== SHARED_AUDIENCE.PERSONAL)?.value || SHARED_AUDIENCE.PERSONAL;
        const nextAudience = itemType.scope === "personal"
            ? SHARED_AUDIENCE.PERSONAL
            : itemType.scope === "company" && form.audience === SHARED_AUDIENCE.PERSONAL
                ? firstManagedSharedAudience
                : form.audience;

        setForm(prev => ({
            ...prev,
            audience: nextAudience,
            itemTypeUuid: itemType.itemTypeUuid,
            taskType: itemType.itemTypeUuid,
            visibilityRecipients: getVisibilityRecipientsForAudience(nextAudience, prev.visibilityRecipients),
            color: getItemTypeColor(itemType.itemTypeUuid, itemTypes, AUDIENCE_COLORS[nextAudience]),
            meeting: itemType.id === TASK_TYPES.MEETING
                ? prev.meeting
                : { ...prev.meeting, enabled: false },
        }));
        setRecipientPickerGroup(null);
        setRecipientSearch("");
    };

    const updateRecipientGroup = (groupKey: VisibilityGroupKey, updater: (groupState: VisibilityRecipientState) => VisibilityRecipientState) => {
        setForm(prev => {
            const current = normaliseVisibilityRecipients(prev.visibilityRecipients);
            return {
                ...prev,
                visibilityRecipients: {
                    ...current,
                    [groupKey]: updater(current[groupKey]),
                },
            };
        });
    };

    const toggleAllRecipients = (groupKey: VisibilityGroupKey) => {
        updateRecipientGroup(groupKey, (groupState) => ({
            all: !groupState.all,
            selectedEmployeeUuids: groupState.all ? groupState.selectedEmployeeUuids : [],
        }));
    };

    const toggleRecipientUser = (groupKey: VisibilityGroupKey, companyUser: CompanyUser) => {
        updateRecipientGroup(groupKey, (groupState) => {
            const selectedEmployeeUuids = groupState.selectedEmployeeUuids.includes(companyUser.uuid)
                ? groupState.selectedEmployeeUuids.filter(uuid => uuid !== companyUser.uuid)
                : [...groupState.selectedEmployeeUuids, companyUser.uuid];
            return {
                all: false,
                selectedEmployeeUuids,
            };
        });
    };

    const openRecipientPicker = (groupKey: VisibilityGroupKey) => {
        setRecipientPickerGroup(prev => prev === groupKey ? null : groupKey);
        setRecipientSearch("");
    };

    const validate = (): boolean => {
        const next: ModalErrors = {};
        if (!form.title.trim()) next.title = "Item title is required.";
        if (!form.startDate) next.startDate = "Start date is required.";
        if (!form.endDate) next.endDate = "End date is required.";
        if (!form.allDay && !form.startTime) next.startTime = "Start time is required.";
        if (!form.allDay && !form.endTime) next.endTime = "End time is required.";
        if (item && !canManageCalendarAudience(user, item.audience) && !hasMeetingParticipantVisibility(item)) {
            next.audience = "Only users with Calendar permission can edit shared calendar items.";
        } else if (!canManageCalendarAudience(user, form.audience) && !hasMeetingParticipantVisibility(form)) {
            next.audience = "Only users with shared calendar permissions can create shared calendar items.";
        }
        const missingRecipients = isScheduledMode ? [] : getAudienceRecipientGroups(form.audience).filter((groupKey) => {
            const groupState = visibilityRecipients[groupKey];
            return !groupState.all && groupState.selectedEmployeeUuids.length === 0;
        });
        if (missingRecipients.length > 0) {
            next.visibilityRecipients = "Choose all users or at least one specific user for each selected visibility group.";
        }
        const start = dayjs(`${form.startDate}T${form.allDay ? "00:00" : form.startTime || "00:00"}`);
        const end = dayjs(`${form.endDate}T${form.allDay ? "23:59" : form.endTime || "00:00"}`);
        if (!end.isAfter(start)) next.endDate = "End must be after start.";
        if (form.meeting.enabled && !form.meeting.timezone) next.meeting = "Meeting timezone is required.";
        if (form.meeting.enabled && form.meeting.passwordEnabled) {
            const passwordLength = form.meeting.password.length;
            if (passwordLength > 0 && (passwordLength < 8 || passwordLength > 72)) {
                next.meeting = "Meeting passwords must contain between 8 and 72 characters.";
            } else if (passwordLength === 0 && !item?.meeting?.passwordEnabled) {
                next.meeting = "Please provide a meeting password.";
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSave = async () => {
        if (savingTask) return;
        if (!validate()) return;
        setSavingTask(true);
        try {
            const uploadedAttachments = await Promise.all(pendingAttachmentFiles.map(uploadCalendarAttachment));
            const formWithUploadedAttachments = {
                ...form,
                attachments: [...form.attachments, ...uploadedAttachments],
            };
            setForm(formWithUploadedAttachments);
            setPendingAttachmentFiles([]);
            await onSave(createCalendarItemFromForm(
                formWithUploadedAttachments,
                item,
                isScheduledMode || formWithUploadedAttachments.meeting.enabled
            ));
        } catch (error) {
            console.error(error);
            setErrors(prev => ({ ...prev, attachments: "Attachment upload failed. Please try again." }));
        } finally {
            setSavingTask(false);
        }
    };

    if (isScheduledMode) {
        return (
            <ModalCard themeMode={themeMode}>
                <ModalHeaderBlock themeMode={themeMode}>
                    <ModalHeaderTop>
                        <ModalTitleBlock>
                            <ModalTitle>Edit Calendar Item</ModalTitle>
                            <SmallText>Update timing, people, links, and visibility for this calendar item.</SmallText>
                        </ModalTitleBlock>
                    </ModalHeaderTop>
                </ModalHeaderBlock>
                <ModalScroll>
                    <ModalSection themeMode={themeMode}>
                        <ModalSectionHeader>
                            <ModalSectionTitle>Item Details</ModalSectionTitle>
                        </ModalSectionHeader>
                        <FormGrid>
                            <div>
                                <Label>Item Title</Label>
                                <TaskFieldInput themeMode={themeMode} value={form.title} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, title: value }))} />
                                {errors.title && <ErrorText>{errors.title}</ErrorText>}
                            </div>
                            <div />
                        </FormGrid>
                        <FormGrid>
                            <div>
                                <Label>Start Date</Label>
                                <CalendarDateInput themeMode={themeMode} variant="task" value={form.startDate} onChange={(value) => setForm(prev => syncRangeFromStartDate(prev, value))} />
                                {errors.startDate && <ErrorText>{errors.startDate}</ErrorText>}
                            </div>
                            {!form.allDay && (
                                <div>
                                    <Label>Start Time</Label>
                                    <TaskFieldInput themeMode={themeMode} type="time" value={form.startTime} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, startTime: value }))} />
                                    {errors.startTime && <ErrorText>{errors.startTime}</ErrorText>}
                                </div>
                            )}
                        </FormGrid>
                        <FormGrid>
                            <div>
                                <Label>End Date</Label>
                                <CalendarDateInput themeMode={themeMode} variant="task" value={form.endDate} onChange={(value) => setForm(prev => syncRangeFromEndDate(prev, value))} />
                                {errors.endDate && <ErrorText>{errors.endDate}</ErrorText>}
                            </div>
                            {!form.allDay && (
                                <div>
                                    <Label>End Time</Label>
                                    <TaskFieldInput themeMode={themeMode} type="time" value={form.endTime} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, endTime: value }))} />
                                    {errors.endTime && <ErrorText>{errors.endTime}</ErrorText>}
                                </div>
                            )}
                        </FormGrid>
                        <FormGrid>
                            <div>
                                <FilterItem as="div" style={{ minHeight: 42 }}>
                                    <FilterLabel style={{ minWidth: 0, flex: 1, textAlign: "left", marginRight: "0.9rem" }}>
                                        All Day
                                    </FilterLabel>
                                    <Toggle
                                        active={form.allDay}
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setForm(prev => prev.allDay
                                                ? {
                                                    ...prev,
                                                    allDay: false,
                                                    startTime: prev.startTime || DEFAULT_START_TIME,
                                                    endTime: prev.endTime || DEFAULT_END_TIME,
                                                }
                                                : {
                                                    ...prev,
                                                    allDay: true,
                                                    endDate: prev.startDate,
                                                    startTime: "",
                                                    endTime: "",
                                                });
                                        }}
                                    />
                                </FilterItem>
                            </div>
                            <div>
                                <Label>Reminder</Label>
                                <CalendarSelectWrap $fullWidth>
                                    <TaskFieldSelect themeMode={themeMode} value={form.reminderOffset} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, reminderOffset: value }))}>
                                        {REMINDER_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </TaskFieldSelect>
                                    <CalendarSelectChevron aria-hidden="true">›</CalendarSelectChevron>
                                </CalendarSelectWrap>
                            </div>
                        </FormGrid>
                    </ModalSection>
                    {supportsMeets && <ModalSection themeMode={themeMode}>
                        <CalendarMeetingSection
                            companyUsers={companyUsers}
                            companyUsersLoading={companyUsersStatus === "loading"}
                            currentUserUuid={getCalendarUserEmployeeUuid(user)}
                            form={form}
                            hasStoredPassword={Boolean(item?.meeting?.passwordEnabled)}
                            setForm={setForm}
                            themeMode={themeMode}
                        />
                        {errors.meeting && <ErrorText>{errors.meeting}</ErrorText>}
                    </ModalSection>}
                    <ModalSection themeMode={themeMode}>
                        <ModalSectionHeader>
                            <ModalSectionTitle>Visibility & Colour</ModalSectionTitle>
                        </ModalSectionHeader>
                        <FormGrid>
                            <div>
                                <Label>Visibility</Label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    {audienceOptions.map(({ value, label }) => (
                                        <CategoryTab
                                            key={value}
                                            type="button"
                                            color={AUDIENCE_COLORS[value]}
                                            active={form.audience === value}
                                            onClick={() => updateScheduledAudience(value)}
                                        >
                                            {label}
                                            {form.audience === value && <CategoryTabCheck src={GreenCheckIcon} alt="" aria-hidden="true" />}
                                        </CategoryTab>
                                    ))}
                                </div>
                                {errors.audience && <ErrorText>{errors.audience}</ErrorText>}
                            </div>
                            <div>
                                <Label>Colour</Label>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    {Object.values(SOURCE_COLORS).slice(0, 6).map(color => (
                                        <CategoryTab
                                            key={color}
                                            type="button"
                                            color={color}
                                            active={form.color === color}
                                            onClick={() => setForm(prev => ({ ...prev, color }))}
                                        >
                                            <ColorSwatch color={color} />
                                        </CategoryTab>
                                    ))}
                                </div>
                            </div>
                        </FormGrid>
                    </ModalSection>
                    <ModalSection themeMode={themeMode}>
                        <ModalSectionHeader>
                            <ModalSectionTitle>Notes</ModalSectionTitle>
                        </ModalSectionHeader>
                        <TaskFieldArea themeMode={themeMode} value={form.description} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, description: value }))} />
                    </ModalSection>
                </ModalScroll>
                <ModalFooter themeMode={themeMode}>
                    <div>
                        {item && (
                            <DangerButton type="button" disabled={itemActionPending} onClick={handleDelete}>
                                {deletingItem ? "Deleting…" : "Delete Item"}
                            </DangerButton>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <ModalCancelButton themeMode={themeMode} type="button" onClick={onClose} disabled={itemActionPending}>Cancel</ModalCancelButton>
                        <LoadingActionButton
                            type="button"
                            isLoading={savingTask}
                            disabled={itemActionPending}
                            minWidth="120px"
                            height="42px"
                            borderRadius="var(--calendar-radius-control)"
                            backgroundColor="var(--calendar-accent)"
                            color="#ffffff"
                            font="normal normal 800 12px/16px Inter, system-ui, sans-serif"
                            onClick={handleSave}
                        >
                            Save Item
                        </LoadingActionButton>
                    </div>
                </ModalFooter>
            </ModalCard>
        );
    }

    return (
        <ModalCard themeMode={themeMode}>
            <ModalHeaderBlock themeMode={themeMode}>
                <ModalHeaderTop>
                    <ModalTitleBlock>
                        <ModalTitle>{item ? "Edit Calendar Item" : "New Calendar Item"}</ModalTitle>
                        <SmallText>Create a calendar item with timing, people, links, and visibility. Automations from other Me Business features will also appear in your calendar automatically.</SmallText>
                    </ModalTitleBlock>
                </ModalHeaderTop>
            </ModalHeaderBlock>
            <ModalScroll>
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Item Type & Visibility</ModalSectionTitle>
                    </ModalSectionHeader>
                    <div>
                        <Label>Item Type</Label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {availableItemTypeOptions.map((itemType) => (
                                <CategoryTab
                                    key={itemType.itemTypeUuid}
                                    type="button"
                                    color={itemType.color}
                                    active={(form.itemTypeUuid || form.taskType) === itemType.itemTypeUuid}
                                    onClick={() => selectItemType(itemType)}
                                >
                                    {itemType.label}
                                    {(form.itemTypeUuid || form.taskType) === itemType.itemTypeUuid && <CategoryTabCheck src={GreenCheckIcon} alt="" aria-hidden="true" />}
                                </CategoryTab>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label>Visibility</Label>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {audienceOptions.map(({ value, label }) => (
                                <CategoryTab
                                    key={value}
                                    type="button"
                                    color={AUDIENCE_COLORS[value]}
                                    active={form.audience === value}
                                    onClick={() => updateAudience(value)}
                                >
                                    {label}
                                    {form.audience === value && <CategoryTabCheck src={GreenCheckIcon} alt="" aria-hidden="true" />}
                                </CategoryTab>
                            ))}
                        </div>
                        {activeRecipientGroups.length > 0 && (
                            <VisibilityTargetGrid>
                                {activeRecipientGroups.map((groupKey) => {
                                    const group = getVisibilityRecipientGroup(groupKey);
                                    const groupState = visibilityRecipients[groupKey];
                                    const pickerOpen = recipientPickerGroup === groupKey;
                                    return (
                                        <VisibilityTargetCard key={groupKey}>
                                            <VisibilityTargetHeader>
                                                <div>
                                                    <VisibilityTargetTitle>
                                                        <ColorSwatch color={group.color} />
                                                        {group.label}
                                                    </VisibilityTargetTitle>
                                                    <SmallText>{getVisibilityRecipientSummary(groupKey, visibilityRecipients)}</SmallText>
                                                </div>
                                                <VisibilityTargetActions>
                                                    <FilterItem as="div" style={{ gap: 10 }}>
                                                        <FilterLabel>All {group.label}</FilterLabel>
                                                        <Toggle
                                                            type="button"
                                                            active={groupState.all}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                toggleAllRecipients(groupKey);
                                                            }}
                                                        />
                                                    </FilterItem>
                                                    <ModalCancelButton
                                                        themeMode={themeMode}
                                                        type="button"
                                                        style={{ height: 38, minWidth: 132 }}
                                                        onClick={() => openRecipientPicker(groupKey)}
                                                    >
                                                        {pickerOpen ? "Close List" : `Choose ${group.label}`}
                                                    </ModalCancelButton>
                                                </VisibilityTargetActions>
                                            </VisibilityTargetHeader>
                                            {pickerOpen && selectedRecipientGroup === groupKey && (
                                                <VisibilityPickerPanel themeMode={themeMode}>
                                                    <VisibilityPickerHeader>
                                                        <div>
                                                            <Label style={{ marginBottom: 4 }}>Choose {group.label}</Label>
                                                            <SmallText>
                                                                Toggle individual users when this item should only be visible to specific {group.label.toLowerCase()}.
                                                            </SmallText>
                                                        </div>
                                                        <ModalCancelButton
                                                            themeMode={themeMode}
                                                            type="button"
                                                            style={{ height: 34, minWidth: 82, padding: "0 0.8rem" }}
                                                            onClick={() => setRecipientPickerGroup(null)}
                                                        >
                                                            Done
                                                        </ModalCancelButton>
                                                    </VisibilityPickerHeader>
                                                    <TaskFieldInput
                                                        themeMode={themeMode}
                                                        value={recipientSearch}
                                                        onChange={({ target: { value } }) => setRecipientSearch(value)}
                                                        placeholder={`Search ${group.label.toLowerCase()}`}
                                                    />
                                                    {companyUsersStatus === "loading" && (
                                                        <VisibilityEmptyState>
                                                            <SmallText>Loading company users…</SmallText>
                                                        </VisibilityEmptyState>
                                                    )}
                                                    {companyUsersStatus === "error" && (
                                                        <FieldHint>Company users could not be loaded.</FieldHint>
                                                    )}
                                                    {companyUsersStatus !== "loading" && (
                                                        <VisibilityUserList>
                                                            {recipientUsers.length === 0 && (
                                                                <VisibilityEmptyState>
                                                                    <SmallText>No company users available.</SmallText>
                                                                </VisibilityEmptyState>
                                                            )}
                                                            {recipientUsers.map((companyUser) => {
                                                                const selected = groupState.selectedEmployeeUuids.includes(companyUser.uuid);
                                                                return (
                                                                    <VisibilityUserRow
                                                                        key={companyUser.uuid}
                                                                        type="button"
                                                                        active={selected}
                                                                        onClick={() => toggleRecipientUser(groupKey, companyUser)}
                                                                    >
                                                                        <VisibilityUserAvatar background={`${group.color}22`} color={group.color}>
                                                                            {getCompanyUserInitials(companyUser)}
                                                                        </VisibilityUserAvatar>
                                                                        <div>
                                                                            <div style={{ fontWeight: 800 }}>{getCompanyUserName(companyUser)}</div>
                                                                            <SmallText>
                                                                                {[companyUser.department, companyUser.employmentPosition, companyUser.email].filter(Boolean).join(" · ") || group.singularLabel}
                                                                            </SmallText>
                                                                        </div>
                                                                        <Toggle as="span" active={selected} />
                                                                    </VisibilityUserRow>
                                                                );
                                                            })}
                                                            {recipientUsers.length === 0 && (
                                                                <VisibilityEmptyState>
                                                                    <SmallText>No {group.label.toLowerCase()} matched this search.</SmallText>
                                                                </VisibilityEmptyState>
                                                            )}
                                                        </VisibilityUserList>
                                                    )}
                                                </VisibilityPickerPanel>
                                            )}
                                        </VisibilityTargetCard>
                                    );
                                })}
                            </VisibilityTargetGrid>
                        )}
                        {errors.audience && <ErrorText>{errors.audience}</ErrorText>}
                        {errors.visibilityRecipients && <ErrorText>{errors.visibilityRecipients}</ErrorText>}
                    </div>
                </ModalSection>
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Item Details</ModalSectionTitle>
                    </ModalSectionHeader>
                    <FormGrid>
                        <div>
                            <Label>Item Title</Label>
                            <TaskFieldInput themeMode={themeMode} value={form.title} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, title: value }))} />
                            {errors.title && <ErrorText>{errors.title}</ErrorText>}
                        </div>
                        <div>
                            <Label>Location</Label>
                            <LocationFieldWrap open={locationSearchOpen && (locationSearchPending || locationSuggestions.length > 0)}>
                                <TaskFieldInput
                                    themeMode={themeMode}
                                    value={form.location}
                                    onFocus={() => {
                                        if (form.location.trim().length >= 3) {
                                            setLocationSearchOpen(true);
                                        }
                                    }}
                                    onBlur={() => {
                                        locationBlurTimeoutRef.current = window.setTimeout(() => {
                                            setLocationSearchOpen(false);
                                        }, 120);
                                    }}
                                    onChange={({ target: { value } }) => {
                                        if (locationBlurTimeoutRef.current) {
                                            window.clearTimeout(locationBlurTimeoutRef.current);
                                        }
                                        setLocationSearchOpen(true);
                                        setForm(prev => ({ ...prev, location: value }));
                                    }}
                                    placeholder="Add location"
                                />
                                {locationSearchOpen && (locationSearchPending || locationSuggestions.length > 0) && (
                                    <LocationSuggestionList themeMode={themeMode}>
                                        {locationSearchPending && (
                                            <LocationSuggestionButton as="div" themeMode={themeMode}>
                                                <div style={{ fontWeight: 700 }}>Searching addresses…</div>
                                            </LocationSuggestionButton>
                                        )}
                                        {!locationSearchPending && locationSuggestions.map((suggestion) => (
                                            <LocationSuggestionButton
                                                key={suggestion.id}
                                                type="button"
                                                themeMode={themeMode}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    if (locationBlurTimeoutRef.current) {
                                                        window.clearTimeout(locationBlurTimeoutRef.current);
                                                    }
                                                    setForm(prev => ({ ...prev, location: suggestion.label }));
                                                    setLocationSuggestions([]);
                                                    setLocationSearchOpen(false);
                                                }}
                                            >
                                                <div style={{ fontWeight: 700 }}>{suggestion.label}</div>
                                                {suggestion.detail && <SmallText>{suggestion.detail}</SmallText>}
                                            </LocationSuggestionButton>
                                        ))}
                                    </LocationSuggestionList>
                                )}
                            </LocationFieldWrap>
                            {!item && getDetectedLocationLabel(locationInfo) && (
                                <FieldHint>Detected from your current location. Start typing to search and replace it with a specific address.</FieldHint>
                            )}
                        </div>
                    </FormGrid>
                    <FormGrid>
                        <div>
                            <Label>Start Date</Label>
                            <CalendarDateInput themeMode={themeMode} variant="task" value={form.startDate} onChange={(value) => setForm(prev => syncTaskRangeFromStartDate(prev, value))} />
                            {errors.startDate && <ErrorText>{errors.startDate}</ErrorText>}
                        </div>
                        {!form.allDay && (
                            <div>
                                <Label>Start Time</Label>
                                <SharedTimeInput
                                    value={form.startTime}
                                    onChange={(value: string) => setForm(prev => ({ ...prev, startTime: value }))}
                                    placeholder="Select time"
                                />
                                {errors.startTime && <ErrorText>{errors.startTime}</ErrorText>}
                            </div>
                        )}
                    </FormGrid>
                    <FormGrid>
                        <div>
                            <Label>End Date</Label>
                            <CalendarDateInput themeMode={themeMode} variant="task" value={form.endDate} onChange={(value) => setForm(prev => syncTaskRangeFromEndDate(prev, value))} />
                            {errors.endDate && <ErrorText>{errors.endDate}</ErrorText>}
                        </div>
                        {!form.allDay && (
                            <div>
                                <Label>End Time</Label>
                                <SharedTimeInput
                                    value={form.endTime}
                                    onChange={(value: string) => setForm(prev => ({ ...prev, endTime: value }))}
                                    placeholder="Select time"
                                />
                                {errors.endTime && <ErrorText>{errors.endTime}</ErrorText>}
                            </div>
                        )}
                    </FormGrid>
                    <FormGrid>
                        <div>
                            <div style={{ height: 0, marginBottom: 0 }} />
                            <FilterItem as="div" style={{ minHeight: 42 }}>
                                <FilterLabel style={{ minWidth: 0, flex: 1, textAlign: "left", marginRight: "0.9rem" }}>
                                    <span
                                        style={{ display: "block", cursor: "pointer" }}
                                        onClick={() => setForm(prev => prev.allDay
                                            ? {
                                                ...prev,
                                                allDay: false,
                                                startTime: prev.startTime || DEFAULT_START_TIME,
                                                endTime: prev.endTime || DEFAULT_END_TIME,
                                            }
                                            : {
                                                ...prev,
                                                allDay: true,
                                                endDate: prev.startDate,
                                                startTime: "",
                                                endTime: "",
                                            })}
                                    >
                                        All Day
                                    </span>
                                </FilterLabel>
                                <Toggle
                                    active={form.allDay}
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setForm(prev => prev.allDay
                                            ? {
                                                ...prev,
                                                allDay: false,
                                                startTime: prev.startTime || DEFAULT_START_TIME,
                                                endTime: prev.endTime || DEFAULT_END_TIME,
                                            }
                                            : {
                                                ...prev,
                                                allDay: true,
                                                endDate: prev.startDate,
                                                startTime: "",
                                                endTime: "",
                                            });
                                    }}
                                />
                            </FilterItem>
                        </div>
                        <div>
                            <div style={{ height: 0, marginBottom: 0 }} />
                            <ModalGradientButton
                                themeMode={themeMode}
                                type="button"
                                disabled={form.allDay}
                                aria-controls="calendar-timezone-calculator"
                                aria-expanded={timezoneCalculatorOpen}
                                title={form.allDay ? "Turn off All Day to use timezone suggestions." : "Open Timezone Calculator"}
                                style={{ width: "100%" }}
                                onClick={openTimezoneCalculator}
                            >
                                {timezoneCalculatorOpen ? "Timezone Calculator Open" : "Open Timezone Calculator"}
                            </ModalGradientButton>
                        </div>
                    </FormGrid>
                    <FormGrid>
                        { IsFeatureReady.calendarGuestInvites() &&
                            <div>
                                <Label>Add Invites</Label>
                                <TaskFieldInput themeMode={themeMode} value={form.invites} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, invites: value }))} placeholder="Names or email addresses" />
                            </div>
                        }
                        <div>
                            <Label>Add URL</Label>
                            <TaskFieldInput themeMode={themeMode} value={form.url} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, url: value }))} placeholder="https://..." />
                        </div>
                    </FormGrid>
                    <FormGrid>
                        <div>
                            <Label>Priority</Label>
                            <CalendarSelectWrap $fullWidth>
                                <TaskFieldSelect themeMode={themeMode} value={form.priority} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, priority: value as TaskPriority }))}>
                                    <option value={TASK_PRIORITIES.HIGH}>High</option>
                                    <option value={TASK_PRIORITIES.MEDIUM}>Medium</option>
                                    <option value={TASK_PRIORITIES.LOW}>Low</option>
                                </TaskFieldSelect>
                                <CalendarSelectChevron aria-hidden="true">›</CalendarSelectChevron>
                            </CalendarSelectWrap>
                        </div>
                        <div>
                            <Label>Repeat</Label>
                            <CalendarSelectWrap $fullWidth>
                                <TaskFieldSelect themeMode={themeMode} value={form.repeat} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, repeat: value as TaskRepeatOption }))}>
                                    <option value={TASK_REPEAT_OPTIONS.NONE}>{TASK_REPEAT_LABEL[TASK_REPEAT_OPTIONS.NONE]}</option>
                                    <option value={TASK_REPEAT_OPTIONS.EVERY_DAY}>{TASK_REPEAT_LABEL[TASK_REPEAT_OPTIONS.EVERY_DAY]}</option>
                                    <option value={TASK_REPEAT_OPTIONS.EVERY_WEEK}>{TASK_REPEAT_LABEL[TASK_REPEAT_OPTIONS.EVERY_WEEK]}</option>
                                    <option value={TASK_REPEAT_OPTIONS.EVERY_MONTH}>{TASK_REPEAT_LABEL[TASK_REPEAT_OPTIONS.EVERY_MONTH]}</option>
                                    <option value={TASK_REPEAT_OPTIONS.EVERY_YEAR}>{TASK_REPEAT_LABEL[TASK_REPEAT_OPTIONS.EVERY_YEAR]}</option>
                                </TaskFieldSelect>
                                <CalendarSelectChevron aria-hidden="true">›</CalendarSelectChevron>
                            </CalendarSelectWrap>
                        </div>
                    </FormGrid>
                    <FormGrid columns={1}>
                        <div>
                            <Label>Reminder</Label>
                            <CalendarSelectWrap $fullWidth>
                                <TaskFieldSelect themeMode={themeMode} value={form.reminderOffset} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, reminderOffset: value }))}>
                                    {REMINDER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </TaskFieldSelect>
                                <CalendarSelectChevron aria-hidden="true">›</CalendarSelectChevron>
                            </CalendarSelectWrap>
                        </div>
                    </FormGrid>
                    {!hideSmartSuggestion && !form.allDay && suggestionDismissed && smartSuggestion && (
                        <SuggestionRestoreRow>
                            <SuggestionRestoreButton type="button" onClick={() => setSuggestionDismissed(false)}>
                                Show Smart Time Block Suggestion
                            </SuggestionRestoreButton>
                        </SuggestionRestoreRow>
                    )}
                    {!hideSmartSuggestion && !form.allDay && !suggestionDismissed && (
                        <SuggestionCard dismissed={!smartSuggestion}>
                            <div>
                                <Label>Smart Time Block Suggestion</Label>
                                {!smartSuggestion && (
                                    <SmallText>No open time block was found in working hours for this day. You can still set your own start and end time.</SmallText>
                                )}
                                {smartSuggestion && (
                                    <>
                                        <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>
                                            {dayjs(`${smartSuggestion.startDate}T${smartSuggestion.startTime}`).format("ddd D MMM · h:mm A")}
                                            {" - "}
                                            {dayjs(`${smartSuggestion.endDate}T${smartSuggestion.endTime}`).format("h:mm A")}
                                        </div>
                                        <SmallText>{smartSuggestion.reason} · {smartSuggestion.durationMinutes} min recommended block.</SmallText>
                                    </>
                                )}
                            </div>
                            {smartSuggestion && (
                                <SuggestionActions>
                                    <ModalGradientButton
                                        themeMode={themeMode}
                                        type="button"
                                        onClick={() => {
                                            setForm(prev => ({
                                                ...prev,
                                                startDate: smartSuggestion.startDate,
                                                startTime: smartSuggestion.startTime,
                                                endDate: smartSuggestion.endDate,
                                                endTime: smartSuggestion.endTime,
                                            }));
                                            setSuggestionDismissed(true);
                                        }}
                                    >
                                        Accept Suggestion
                                    </ModalGradientButton>
                                    <ModalCancelButton themeMode={themeMode} type="button" onClick={() => setSuggestionDismissed(true)}>
                                        Ignore
                                    </ModalCancelButton>
                                </SuggestionActions>
                            )}
                        </SuggestionCard>
                    )}
                    {!form.allDay && timezoneCalculatorOpen && (
                        <SuggestionCard
                            ref={timezoneCalculatorRef}
                            id="calendar-timezone-calculator"
                            dismissed={timezoneAttendees.length === 0 || timezoneCalculator.recommendations.length === 0}
                        >
                            <div>
                                <Label>Timezone Calculator</Label>
                                <SmallText>
                                    {timezoneAttendees.length > 0
                                        ? "Scores the selected date against your timezone and configured attendee timezones. Green is prime work-hours overlap, amber is workable."
                                        : "No attendee timezones configured yet. Add attendee timezones in the Calendar sidebar to unlock overlap recommendations."}
                                </SmallText>
                            </div>
                            {timezoneAttendees.length > 0 && (
                                <>
                                    <TimezoneMatchList>
                                        {timezoneCalculator.matchZones.map((zone) => (
                                            <TimezoneMatchItem key={zone.timezone}>
                                                <TimezoneFlagBadge aria-hidden="true">{getTimezoneFlag(zone.timezone)}</TimezoneFlagBadge>
                                                <div>
                                                    <TimezoneMatchRole>{zone.role}</TimezoneMatchRole>
                                                    <div style={{ fontWeight: 800, fontSize: 13 }}>{zone.city}</div>
                                                    <SmallText>{zone.label}</SmallText>
                                                </div>
                                            </TimezoneMatchItem>
                                        ))}
                                    </TimezoneMatchList>
                                    <TimezoneTimeline>
                                        {timezoneCalculator.hours.map((slot) => (
                                            <TimezoneHourButton
                                                key={slot.hour}
                                                type="button"
                                                active={form.startDate === slot.startDate && form.startTime === slot.startTime}
                                                title={slot.breakdown.map(item => `${item.city}: ${item.localLabel}`).join("\n")}
                                                onClick={() => applyTimezoneSuggestion(slot)}
                                            >
                                                <TimezoneHourBar score={slot.score} tone={slot.tone} height={Math.max(8, Math.round((slot.score / Math.max(slot.maxScore, 1)) * 56))} />
                                                <span style={{ fontSize: 9, fontWeight: 800 }}>{slot.hour}</span>
                                            </TimezoneHourButton>
                                        ))}
                                    </TimezoneTimeline>
                                    <SourceChipRow>
                                        <Badge background="rgba(66, 231, 182, 0.16)" color="#22c55e">Prime overlap</Badge>
                                        <Badge background="rgba(248, 200, 96, 0.18)" color="#f59e0b">Decent</Badge>
                                        <Badge background="var(--calendar-surface-tertiary)" color="var(--calendar-text-soft)">Avoid</Badge>
                                    </SourceChipRow>
                                    {timezoneCalculator.recommendations.length > 0 ? (
                                        <TimezoneSuggestionGrid>
                                            {timezoneCalculator.recommendations.map((slot) => (
                                                <ModalCancelButton
                                                    key={slot.hour}
                                                    themeMode={themeMode}
                                                    type="button"
                                                    style={{ minWidth: 0, height: "auto", minHeight: 52, textAlign: "left" }}
                                                    onClick={() => applyTimezoneSuggestion(slot)}
                                                >
                                                    {slot.start.format("h:mm A")}
                                                    <SmallText>{slot.tone === "prime" ? "Prime overlap" : "Decent overlap"}</SmallText>
                                                </ModalCancelButton>
                                            ))}
                                        </TimezoneSuggestionGrid>
                                    ) : (
                                        <SmallText>No strong overlap found for this date. Try an earlier/later hour or adjust attendee timezones.</SmallText>
                                    )}
                                </>
                            )}
                        </SuggestionCard>
                    )}
                </ModalSection>
                {supportsMeets && <ModalSection themeMode={themeMode}>
                    <CalendarMeetingSection
                        companyUsers={companyUsers}
                        companyUsersLoading={companyUsersStatus === "loading"}
                        currentUserUuid={getCalendarUserEmployeeUuid(user)}
                        form={form}
                        hasStoredPassword={Boolean(item?.meeting?.passwordEnabled)}
                        setForm={setForm}
                        themeMode={themeMode}
                    />
                    {errors.meeting && <ErrorText>{errors.meeting}</ErrorText>}
                </ModalSection>}
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Attachments</ModalSectionTitle>
                        <ModalSectionNote>Add supporting files or keep notes about attachments for this item.</ModalSectionNote>
                    </ModalSectionHeader>
                    <AttachmentRow>
                        <ModalPrimaryButton
                            themeMode={themeMode}
                            type="button"
                            onClick={() => attachmentInputRef.current?.click()}
                        >
                            Browse Files
                        </ModalPrimaryButton>
                        <HiddenFileInput ref={attachmentInputRef} type="file" multiple onChange={handleAttachmentBrowse} />
                        <SmallText>{form.attachments.length + pendingAttachmentFiles.length > 0 ? "Selected files will upload when you save this item." : "No files selected yet."}</SmallText>
                    </AttachmentRow>
                    {(form.attachments.length > 0 || pendingAttachmentFiles.length > 0) && (
                        <AttachmentList>
                            {form.attachments.map((attachment) => (
                                <AttachmentBadge key={attachment.uuid} themeMode={themeMode}>
                                    <AttachmentName>
                                        <span>{attachment.filename}</span>
                                    </AttachmentName>
                                    <AttachmentRemove type="button" onClick={() => removeAttachment(attachment.uuid)}>×</AttachmentRemove>
                                </AttachmentBadge>
                            ))}
                            {pendingAttachmentFiles.map((file, index) => (
                                <AttachmentBadge key={`${file.name}-${file.lastModified}-${index}`} themeMode={themeMode}>
                                    <AttachmentName>
                                        <span>{file.name}</span>
                                    </AttachmentName>
                                    <AttachmentRemove type="button" onClick={() => removePendingAttachment(index)}>×</AttachmentRemove>
                                </AttachmentBadge>
                            ))}
                        </AttachmentList>
                    )}
                    {errors.attachments && <ErrorText>{errors.attachments}</ErrorText>}
                </ModalSection>
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Notes</ModalSectionTitle>
                        <ModalSectionNote>Optional context for the assignee or team</ModalSectionNote>
                    </ModalSectionHeader>
                    <div>
                        <TaskFieldArea themeMode={themeMode} value={form.description} onChange={({ target: { value } }) => setForm(prev => ({ ...prev, description: value }))} />
                    </div>
                </ModalSection>
            </ModalScroll>
            <ModalFooter themeMode={themeMode}>
                <div>
                    {item && (
                        <DangerButton type="button" disabled={itemActionPending} onClick={handleDelete}>
                            {deletingItem ? "Deleting…" : "Delete Item"}
                        </DangerButton>
                    )}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <ModalCancelButton themeMode={themeMode} type="button" onClick={onClose} disabled={itemActionPending}>Cancel</ModalCancelButton>
                    <LoadingActionButton
                        type="button"
                        isLoading={savingTask}
                        disabled={itemActionPending}
                        minWidth="120px"
                        height="42px"
                        borderRadius="var(--calendar-radius-control)"
                        backgroundColor="var(--calendar-accent)"
                        color="#ffffff"
                        font="normal normal 800 12px/16px Inter, system-ui, sans-serif"
                        onClick={handleSave}
                    >
                        {item ? "Save Item" : "Create Item"}
                    </LoadingActionButton>
                </div>
            </ModalFooter>
        </ModalCard>
    );
}
